// Which service and provider a scheduling message is asking for.
//
// The panel used to open on mappings[0] — the first row the API happened to
// return — so a phone call with the doctor presented as a lab draw with the
// nurse, on every task.
//
// There is an older guesser server-side (inferServiceType in
// aiAppointmentScheduling), but it tests the generic word "appointment" in its
// first branch, so nearly every scheduling message came back as a 90-minute
// Consultation, and it has no word for a phone call at all. Specific beats
// generic here, which is the ordering this one keeps.
//
// Being wrong is cheap by design: this only decides which slots are listed. It
// books nothing. A bad guess costs one change of the dropdown — which is what
// the old behaviour cost every single time.

type MappingLike = {
  id: string;
  service?: { name?: string } | null;
  specialist?: { name?: string } | null;
};

// A message naming the doctor and a visit wants the doctor, whatever else it
// mentions. Real example, from the thread history: "schedule my annual lab test
// and wellness checkup with Dr Levitan" — labs are part of it, but it is the
// doctor's appointment, and sending it to the nurse's lab slot is the wrong
// half of the request.
const DOCTOR_VISIT = /\b(wellness|annual|physical|check[- ]?up|consult)\b[^.!?]*\b(dr\.?|doctor|levitan)\b|\b(dr\.?|doctor|levitan)\b[^.!?]*\b(wellness|annual|physical|check[- ]?up|consult)\b/i;

// Most specific first. "labs drawn before the visit" is a lab draw, not a visit.
const RULES: Array<{ service: RegExp; words: RegExp }> = [
  { service: /ekg|ecg/i, words: /\b(ekg|ecg|heart trace)\b/i },
  { service: /lab|draw|blood/i, words: /\b(lab|labs|bloodwork|blood work|blood|draw|drawn)\b/i },
  // Asking to be called, not reporting that a phone rang. "Answered the phone"
  // and "calling in a refill" are both real messages that the looser version of
  // this rule claimed as telehealth requests.
  { service: /telehealth|virtual|video|phone/i, words: /\b(by phone|over the phone|on the phone|phone call|phone appointment|call me|calling me|speak with him|speak to him|speak with the doctor|telehealth|video visit|virtual)\b/i },
  { service: /consult|visit|physical|wellness/i, words: /\b(consult|consultation|physical|wellness|annual|check[- ]?up|come in|in person|appointment|visit)\b/i },
];

const named = (mapping: MappingLike): string =>
  `${mapping?.service?.name || ''}`;

// A doctor's own service, preferred over a nurse's, for anything unrecognised —
// the old fallback landed on the lab, which is the one place a stray message
// should never go.
function doctorFallback(mappings: MappingLike[]): string {
  const doctor = mappings.find((m) => /\bdr\.?\b|doctor/i.test(String(m?.specialist?.name || '')));
  if (doctor) {
    const consult = mappings.find(
      (m) => m.specialist?.name === doctor.specialist?.name && /consult|visit|physical/i.test(named(m)),
    );
    return (consult || doctor).id;
  }
  return mappings[0]?.id || '';
}

export function suggestServiceMapping(messageText: string, mappings: MappingLike[]): string {
  const usable = (mappings || []).filter((m) => m && m.id);
  if (usable.length === 0) return '';
  const text = String(messageText || '');

  if (DOCTOR_VISIT.test(text)) return doctorFallback(usable);

  for (const rule of RULES) {
    if (!rule.words.test(text)) continue;
    // Only offer what the practice has actually configured.
    const match = usable.find((m) => rule.service.test(named(m)));
    if (match) return match.id;
  }
  return doctorFallback(usable);
}
